/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, CommandService } from '@patsnap/tahiti-core';
import type { DateModelType } from 'element-plus';
import type {
    NoveltySearchInputState,
    NoveltySearchStartResult,
} from '../novelty-search-input-state';
import {
    CreateNoveltyCheckTaskApi,
    type CreateNoveltyCheckTaskRequest,
    type ImageToTextResult,
} from '@/infrastructure/api-functions/core-drafting-api/create-novelty-check-task-api';
import {
    DetectLanguageApi,
} from '@/infrastructure/api-functions/core-drafting-api/detect-language-api';
import {
    TranslateTextApi,
} from '@/infrastructure/api-functions/core-drafting-api/translate-text-api';
import { generateUUID } from '@telecom/shared/utils/uuid';
import { formatDateToYYYYMMDD } from '@/domains/fto-report/utils/date-format';
import { getCurrentLanguage } from '@telecom/shared/utils/language-tool';

/**
 * 查新检索输入状态服务实现
 */
@injectable()
export class NoveltySearchInputStateImpl implements NoveltySearchInputState {
    private _input = '';
    private _publicationDate: [DateModelType, DateModelType] = ['', ''];
    private _isLoading = false;
    private _imageToTextResult: ImageToTextResult | undefined;

    private _onInputChangeEmitter = new Emitter<string>();
    private _onPublicationDateChangeEmitter = new Emitter<[DateModelType, DateModelType]>();
    private _onLoadingChangeEmitter = new Emitter<boolean>();
    private _onImageToTextResultChangeEmitter = new Emitter<ImageToTextResult | undefined>();

    readonly onInputChange = this._onInputChangeEmitter.event;
    readonly onPublicationDateChange = this._onPublicationDateChangeEmitter.event;
    readonly onLoadingChange = this._onLoadingChangeEmitter.event;
    readonly onImageToTextResultChange = this._onImageToTextResultChangeEmitter.event;

    constructor(
        @inject(CreateNoveltyCheckTaskApi)
        private readonly _createNoveltyCheckTaskApi: CreateNoveltyCheckTaskApi,
        @inject(DetectLanguageApi)
        private readonly _detectLanguageApi: DetectLanguageApi,
        @inject(TranslateTextApi)
        private readonly _translateTextApi: TranslateTextApi,
        @inject(CommandService)
        private readonly _commandService: CommandService,
    ) {}

    getInput(): string {
        return this._input;
    }

    setInput(input: string): void {
        if (this._input !== input) {
            this._input = input;
            this._onInputChangeEmitter.fire(input);
        }
    }

    getPublicationDate(): [DateModelType, DateModelType] {
        return this._publicationDate;
    }

    setPublicationDate(dates: [DateModelType, DateModelType]): void {
        this._publicationDate = dates;
        this._onPublicationDateChangeEmitter.fire(dates);
    }

    isLoading(): boolean {
        return this._isLoading;
    }

    getImageToTextResult(): ImageToTextResult | undefined {
        return this._imageToTextResult;
    }

    setImageToTextResult(result: ImageToTextResult | undefined): void {
        this._imageToTextResult = result;
        this._onImageToTextResultChangeEmitter.fire(result);
        const image2text = result?.text;
        if (!image2text) {
            return;
        }
        const originalInput = this._input;
        if (originalInput.includes(image2text)) {
            return;
        }
        this.setInput(this._input+image2text);
    }

    /**
     * 将界面语言代码转换为翻译 API 所需的语言代码
     * @private
     */
    private _getTargetLanguageCode(): string {
        const uiLang = getCurrentLanguage();
        // 映射界面语言到标准语言代码
        const langMap: Record<string, string> = {
            cn: 'zh', // 简体中文
            en: 'en', // 英文
            jp: 'ja', // 日语
            tw: 'zh', // 繁体中文（也使用 zh）
        };
        return langMap[uiLang] || 'zh'; // 默认返回中文
    }

    async startNoveltySearch(): Promise<NoveltySearchStartResult> {
        try {
            this._isLoading = true;
            this._onLoadingChangeEmitter.fire(true);

            // 检测输入语言
            const languageDetectResult = await this._detectLanguageApi.detectLanguage({
                input: this._input,
            });

            // 用于创建任务的翻译文本（如果有）
            let translationInput: string | undefined;

            // 如果检测到的语言不是中文或英文，则需要翻译
            if (languageDetectResult !== 'CN' && languageDetectResult !== 'EN') {
                try {
                    // 调用翻译 API，将小语种翻译成用户界面语言
                    const translateResult = await this._translateTextApi.translateText({
                        text: this._input,
                        from: languageDetectResult.toLowerCase(),
                        to: this._getTargetLanguageCode(),
                    });

                    // 显示小语种翻译提示对话框
                    const result = await this._commandService.executeCommand(
                        'novelty:open-minor-language-translation-notice-dialog',
                        { translatedText: translateResult.text },
                    );

                    // 用户确认后，保存翻译后的文本
                    translationInput = result.translatedInput;
                } catch (error) {
                    // 用户取消对话框，终止任务创建
                    throw new Error('User cancelled minor language translation');
                }
            }
            // 如果返回 'CN' 或 'EN'，直接使用原始输入，translationInput 保持 undefined

            // 生成任务ID
            const taskId = generateUUID();

            // 准备API请求参数
            const request: CreateNoveltyCheckTaskRequest = {
                // 始终传递用户的原始输入
                input: this._input,
                task_id: taskId,
                pbd_start: formatDateToYYYYMMDD(this._publicationDate[0] as Date | null),
                pbd_end: formatDateToYYYYMMDD(this._publicationDate[1] as Date | null),
            };

            // 如果有翻译文本，添加到请求中
            if (translationInput) {
                request.translation_input = translationInput;
            }

            // 如果有图片转文本结果，添加到请求中
            if (this._imageToTextResult) {
                request.image_to_text_result = this._imageToTextResult;
            }

            // 调用API创建任务
            await this._createNoveltyCheckTaskApi.createNoveltyCheckTask(request);

            // 准备埋点数据（使用翻译后的文本或原始文本）
            const trackingData = {
                techEssential: translationInput?.slice(0,1500) || this._input.slice(0,1500),
                pbdStart: formatDateToYYYYMMDD(this._publicationDate[0] as Date | null),
                pbdEnd: formatDateToYYYYMMDD(this._publicationDate[1] as Date | null),
            };

            return {
                taskId,
                trackingData,
            };
        } finally {
            this._isLoading = false;
            this._onLoadingChangeEmitter.fire(false);
        }
    }

    reset(): void {
        this.setInput('');
        this.setPublicationDate(['', '']);
        this.setImageToTextResult(undefined);
        this._isLoading = false;
        this._onLoadingChangeEmitter.fire(false);
    }
}
